import { z } from "zod";
import { LIMITS } from "../limits";

// Building blocks used by every request schema. Strings are trimmed BEFORE
// length checks (API.md section 1).

export const uuid = z.string().uuid("Must be a valid id.");

/** A required, trimmed string of 1..max characters. */
export const requiredText = (max: number, label = "This field") =>
  z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .max(max, `${label} must be ${max} characters or fewer.`);

/** An optional trimmed string of 0..max characters (empty allowed). */
export const optionalText = (max: number, label = "This field") =>
  z.string().trim().max(max, `${label} must be ${max} characters or fewer.`);

export const email = z
  .string()
  .trim()
  .toLowerCase()
  .max(LIMITS.email, "Email must be 254 characters or fewer.")
  .email("Enter a valid email address.");

/** A valid email address, or "" (for optional contact emails). */
export const emailOrEmpty = z.union([email, z.literal("")]);

export const password = z
  .string()
  .min(LIMITS.passwordMin, `Password must be at least ${LIMITS.passwordMin} characters.`)
  .max(LIMITS.passwordMax, `Password must be ${LIMITS.passwordMax} characters or fewer.`);

/** "YYYY-MM-DD", checked to be a real calendar date. */
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD.")
  .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, "Enter a real date.");

/** An integer amount of euro cents between 0 and max. */
export const cents = (max: number) =>
  z
    .number({ invalid_type_error: "Enter an amount." })
    .int("Amounts are whole cents.")
    .min(0, "The amount cannot be negative.")
    .max(max, "The amount is too large.");

/** A number between 0 and max, in steps of 0.5. */
export const halfStep = (max: number) =>
  z
    .number({ invalid_type_error: "Enter a number." })
    .min(0, "Cannot be negative.")
    .max(max, `Must be ${max} or less.`)
    .refine((n) => Number.isInteger(n * 2), "Use steps of 0.5.");

// ---------- v2 building blocks ----------

/** "YYYY-MM", a real month. */
export const isoMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use the format YYYY-MM.");

/** An http(s):// web address of at most `max` characters, or "" (API.md: urls are http(s) only). */
export const httpUrlOrEmpty = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `The link must be ${max} characters or fewer.`)
    .refine((s) => s === "" || isHttpUrl(s), "Enter a web address that starts with http:// or https://.");

export function isHttpUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.length > 0;
  } catch {
    return false;
  }
}

/** Query-string booleans: only "true" and "false" are accepted. */
export const queryBoolean = z.enum(["true", "false"]).transform((v) => v === "true");

/** A search box in a query string (≤ 100 characters, trimmed). */
export const searchQuery = z.string().trim().max(LIMITS.searchQuery);

/** end >= start when both are set; used as a superRefine on objects with startDate/endDate. */
export function checkDateOrder(
  value: { startDate?: string | null; endDate?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.startDate && value.endDate && value.endDate < value.startDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["endDate"], message: "The end date must be on or after the start date." });
  }
}

/** Rejects `{}` for PATCH bodies. The API maps this to 400 EMPTY_PATCH. */
export const EMPTY_PATCH_MESSAGE = "Nothing to change.";
export function nonEmptyPatch<T extends z.ZodRawShape>(shape: T) {
  return z
    .object(shape)
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: EMPTY_PATCH_MESSAGE });
}

// ---------- Error body (API.md section 2) ----------

export type ErrorCode =
  | "VALIDATION_FAILED"
  /** MD-F6: a supplier cost on an activity with an open contract or order of that supplier: does it pay it? */
  | "PAYS_CONTRACT"
  | "TEAM_MEMBER"
  | "LEVEL_IN_USE"
  | "LEVEL_MISSING"
  | "RESULT_HAS_INDICATORS"
  | "RESULT_IN_USE"
  | "VERSION_CONFLICT"
  | "LINE_IN_USE"
  | "CV_PAPERS_CONFIRM"
  | "MINUTES_TOO_EARLY"
  | "NEEDS_APPROVAL"
  | "REPORT_NOT_EDITABLE"
  | "EMPTY_PATCH"
  | "NOTE_REQUIRED"
  | "LEAD_NOT_MEMBER"
  | "WRONG_PASSWORD"
  | "UNAUTHENTICATED"
  | "INVALID_CREDENTIALS"
  | "SIGN_IN_REQUIRED"
  | "FORBIDDEN"
  | "TRIAL_ENDED"
  | "PAYMENT_DETAILS_REQUIRED"
  | "PAYMENT_FAILED"
  | "BILLING_DETAILS_REQUIRED"
  | "VAT_CHECK_UNAVAILABLE"
  | "TRIAL_NOT_CANCELLABLE"
  | "NOT_REMOVABLE"
  | "DEMO_READ_ONLY"
  | "TRIAL_ALREADY_EXTENDED"
  | "TRIAL_ALREADY_USED"
  | "NOT_A_TRIAL"
  | "NOT_WAITING"
  | "NOT_CONTRACT_LEAD"
  /** Four eyes: the person who entered an expense cannot approve it. */
  | "SELF_APPROVAL"
  /** Fine-grained access: a lead or admin withheld this permission on the project. */
  | "NOT_PERMITTED"
  /** Fine-grained access: a text still shows "(amount hidden)"; saving it would erase the amount. */
  | "MONEY_HIDDEN"
  /** Fine-grained access: a text still shows "(email hidden)"; saving it would erase the address. */
  | "PERSONAL_HIDDEN"
  | "BAD_ORIGIN"
  | "NO_ORG"
  | "SIGNUP_DISABLED"
  | "BOT_CHECK_FAILED"
  | "NOT_FOUND"
  | "INVITATION_INVALID"
  | "RESET_TOKEN_INVALID"
  | "SIGNUP_REFUSED"
  | "CONTRACT_EXISTS"
  | "CONTRACT_CLOSED"
  | "INVALID_STATE"
  | "INVALID_TRANSITION"
  | "TOR_NOT_EDITABLE"
  | "TOR_INCOMPLETE"
  | "TOR_NOT_DELETABLE"
  | "BUDGET_FROM_LINES"
  | "TOO_MANY_LINES"
  | "EXPORT_NOT_AVAILABLE"
  /** GL4: AI reading was asked for where it is off, over its monthly cap or not on this server. */
  | "AI_UNAVAILABLE"
  | "ALREADY_MEMBER"
  /** Fine-grained access: owners and admins always have full access; there is nothing to set. */
  | "ALREADY_FULL"
  | "LAST_OWNER"
  | "INVITATION_EMAIL_MISMATCH"
  // contract manager view (modules/cm)
  | "ALREADY_INVITED"
  | "NOT_PENDING"
  | "ALREADY_DECIDED"
  | "ALREADY_SHARED"
  | "NO_CONTRACT_MANAGER"
  | "UNSUPPORTED_MEDIA_TYPE"
  | "TOO_MANY_ATTEMPTS"
  | "INTERNAL"
  // v2 (API.md 6.0)
  | "WRONG_CONTRACT"
  | "LOGFRAME_INVALID"
  // Tags and stakeholders (round 4)
  | "TAG_ARCHIVED"
  | "TAG_NOT_HERE"
  | "ONE_TAG_PER_GROUP"
  | "TAG_IN_USE"
  | "POSSIBLE_DUPLICATE"
  | "TOR_NOT_APPROVED"
  | "EXPERT_EXISTS"
  | "EXPERT_ALREADY_ON_CONTRACT"
  | "EXPERT_IN_USE"
  | "EXPERT_HAS_WORK"
  | "TIMESHEET_APPROVED"
  | "EVENT_ALREADY_LINKED"
  | "EVENT_HAS_REGISTRATIONS"
  | "EVENT_FULL"
  | "FORM_CLOSED"
  | "CONTACT_EXISTS"
  | "STAKEHOLDER_ORG_EXISTS"
  | "INDICATOR_HAS_READINGS"
  | "INDICATOR_HAS_QUESTIONS"
  | "INDICATOR_HAS_EVENTS"
  | "REPORT_FINAL"
  | "REPORT_NOT_DELETABLE"
  // v3 (API-v3 2)
  | "PASSWORD_TOO_WEAK"
  | "CODE_INVALID"
  | "TERMS_REQUIRED"
  | "FILE_TYPE_NOT_ALLOWED"
  | "FILE_TOO_LARGE"
  | "IMAGE_TOO_SMALL"
  | "IBAN_INVALID"
  | "NAME_MISMATCH"
  | "DECLARATION_REQUIRED"
  | "MAPPING_INVALID"
  | "PARSE_FAILED"
  | "DATE_BEFORE_SUBMISSION"
  | "MFA_REQUIRED"
  | "MFA_ENROL_REQUIRED"
  | "TWO_STEP_ALREADY_SET"
  | "AUTHENTICATOR_REQUIRED"
  | "CURRENT_CODE_REQUIRED"
  | "SSO_EMAIL_NOT_VERIFIED"
  | "SSO_NOT_MEMBER"
  | "FILE_NOT_SCANNED"
  | "EMAIL_NOT_VERIFIED"
  | "LINK_INVALID"
  | "SSO_NO_ACCOUNT"
  | "SSO_FAILED"
  | "EXPERT_ACCOUNT"
  | "STAFF_ACCOUNT"
  | "NOT_GRANTED"
  | "CODE_UNKNOWN"
  | "SHARE_INVALID"
  | "TOKEN_INVALID"
  | "EMAIL_TAKEN"
  | "PROJECT_CLOSED"
  | "EVENT_CLOSED"
  | "HISTORY_LOCKED"
  | "CODE_EXISTS"
  | "ALREADY_ASSIGNED"
  | "OVER_BUDGET"
  | "TIMESHEET_LOCKED"
  | "MONTH_NOT_SIGNED"
  | "DECLARATION_NOT_SIGNED"
  | "BLOCKERS_OPEN"
  | "REPORT_NOT_APPROVED"
  | "AMENDMENT_TOO_LATE"
  | "FINAL_REPORT_NOT_APPROVED"
  | "ALREADY_ANSWERED"
  | "ALREADY_ASKED"
  | "CAPACITY_FULL"
  | "WAITING_LIST_FIRST"
  | "STALE_EDIT"
  | "LOCKED_UNTIL_TOR_APPROVED"
  | "RSVP_FORM_DRAFT"
  | "DUPLICATE_EMAIL"
  /** X-67: a stakeholder with an expert's address (Add person asks first). */
  | "EXPERT_EMAIL"
  | "IMPORT_NOT_READY"
  | "PAYLOAD_TOO_LARGE"
  | "NOT_BUILT_YET"
  | "NOT_READY"
  // billing (modules/billing)
  | "BILLING_OFF"
  | "PAYMENT_PROVIDER_ERROR"
  | "ALREADY_PAYING"
  // experts (Marius round 5): no declaration without an email, no empty timesheet without a warning
  | "NO_EMAIL"
  | "EMPTY_TIMESHEET"
  // approvals and contract (Marius round 5, wave E): ToR reminders, notification letters, CVs kept with the expert
  | "NO_ONE_TO_REMIND"
  | "REMINDED_TODAY"
  | "NOTIFICATION_LETTER"
  | "CV_NOT_A_DOCUMENT"
  /** Onboarding (Task 0.3): a section cannot be confirmed while something in it is undecided (a conflict card). */
  | "section_blocked"
  /** Onboarding (Task 3.1): a blocking open item cannot be marked done; it is fixed in its section. */
  | "open_item_blocking"
  /** Onboarding: the draft item was removed (merged or deleted); restore it first. */
  | "ITEM_REMOVED"
  /** Onboarding: two items that cannot be merged (the same item, or items of different kinds). */
  | "MERGE_NOT_ALLOWED"
  /** Onboarding (Task 1.2): a file whose items were already checked or edited cannot be read again, moved or removed; details { count }. */
  | "file_has_edits"
  /** Onboarding (Task 3.2): Start project is refused while a blocking open item is left; details { blockers }. */
  | "not_ready"
  /** Onboarding (Task 3.2): the project was started already (a second click); details { projectId }. */
  | "already_started"
  /** Onboarding (Task 3.4): documents are added after the start only to a project started through the onboarding. */
  | "not_onboarded"
  /** Onboarding (Task 3.4): a late item that suggests no change cannot be accepted or kept. */
  | "no_change"
  /** Onboarding (Task 3.4): a section is confirmed again only once its documents are read. */
  | "still_reading";

export type FieldErrors = { fieldErrors: Record<string, string[]>; formErrors: string[] };

export type ApiErrorBody = {
  error: { code: ErrorCode; message: string; details?: unknown };
};

/** A reference to a person that survives the person being deleted. */
export type ActorRef = { id: string | null; name: string };
export type UserRef = { id: string; name: string; email: string };

/** List responses are always wrapped: `{ items: [...] }`. */
export type ItemsResponse<T> = { items: T[] };
